import { useMemo, useState } from 'react'
import type { Person } from '../lib/types'

/**
 * The cold-start nudge (v0.20.0, D-119).
 *
 * Measured problem: 0 stories and 2 photos across the whole archive. A tree of
 * names and dates is a database; faces and memories are what make someone open it
 * a second time. This asks for ONE specific thing about ONE specific person rather
 * than "add content" — a request you can actually answer in thirty seconds.
 *
 * Deliberately shown only to people who can edit, only when the archive is genuinely
 * thin, and it rotates so it doesn't become wallpaper.
 */
export default function SeedNudge({ persons, canEdit, photoCount, storyCount, onPick }: {
  persons: Person[]; canEdit: boolean; photoCount: number; storyCount: number
  onPick: (p: Person) => void
}) {
  const [hidden, setHidden] = useState(() => {
    const at = Number(localStorage.getItem('fa_seed_nudge_at') || 0)
    return at > 0 && Date.now() - at < 3 * 864e5   // rest a few days after dismissal
  })

  // the archive is "thin" while there is less than one photo per five people
  const thin = photoCount < Math.max(3, persons.length / 5) || storyCount === 0

  const target = useMemo(() => {
    const faceless = persons.filter(p => !p.avatar_media_id && !p.photo_url)
    if (!faceless.length) return null
    // prefer the oldest generation — the people whose faces are most at risk of
    // being lost, and the ones relatives most want to see
    const withBirth = faceless.filter(p => p.birth_date)
    const pool = withBirth.length ? withBirth : faceless
    const sorted = [...pool].sort((a, b) => String(a.birth_date || '9999').localeCompare(String(b.birth_date || '9999')))
    // rotate daily so it isn't always the same face
    const idx = Math.floor(Date.now() / 864e5) % Math.min(sorted.length, 5)
    return sorted[idx]
  }, [persons])

  if (hidden || !canEdit || !thin || !target) return null

  const name = `${target.first_name} ${target.last_name || ''}`.trim()
  const ask = storyCount === 0
    ? `Do you have a photo of ${name} — or one memory of them worth keeping?`
    : `Is there a photo of ${name} anywhere? A face turns a name into a person.`

  return (
    <div className="card-glass p-4 mb-4 border-gold-500/20 fade-up">
      <h3 className="font-display text-base font-semibold text-slate-100 mb-1">🕰️ One small thing</h3>
      <p className="text-xs text-slate-400 mb-3">{ask}</p>
      <div className="flex gap-2">
        <button onClick={() => onPick(target)} className="btn-gold !py-2 text-sm">Open {target.first_name}</button>
        <button onClick={() => { localStorage.setItem('fa_seed_nudge_at', String(Date.now())); setHidden(true) }}
          className="btn-ghost !py-2 text-sm">Another time</button>
      </div>
    </div>
  )
}
